import React, { useState, useEffect, useCallback } from 'react';
import { useLast10 } from '../../contexts/Last10Context';
import { calcDaysStalled } from '../../services/last10Storage';

/**
 * FinishModePanel
 *
 * Full-screen execution overlay. Triggered when a user activates Finish Mode
 * on a goal in the 80–99% completion range.
 *
 * Displays:
 *  - Current completion percentage with microcopy
 *  - Remaining (incomplete) tasks only
 *  - Days stalled warning (if applicable)
 *  - Exit control
 *
 * Props:
 *   goal       Object    Goal document (must have .id, .title, .last10EnteredAt…)
 *   progress   Object    From getGoalProgress: { totalTasks, completedTasks, completionPercentage, … }
 *   tasks      Array     All tasks linked to this goal (filtered here to incomplete)
 *   onClose    Function  Called when the user exits Finish Mode
 */
const FinishModePanel = ({ goal, progress, tasks = [], onClose }) => {
    const { getMicrocopy, deactivateFinishMode } = useLast10();

    const [closing, setClosing] = useState(false);

    const pct = progress?.completionPercentage ?? 0;
    const remaining = tasks.filter(t => !t.completed && t.status !== 'completed');
    const daysStalled = calcDaysStalled(goal);
    const microcopy = getMicrocopy(pct);

    // ── Close handler ──────────────────────────────────────────────────────

    const handleClose = useCallback(async () => {
        setClosing(true);
        // Small delay for exit animation feel
        await new Promise(r => setTimeout(r, 200));
        await deactivateFinishMode(goal?.id);
        onClose?.();
    }, [deactivateFinishMode, goal?.id, onClose]);

    // Close on Escape
    useEffect(() => {
        const handler = (e) => {
            if (e.key === 'Escape') handleClose();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [handleClose]);

    // Prevent background scroll
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = ''; };
    }, []);

    if (!goal) return null;

    return (
        <div
            className={`fixed inset-0 z-50 flex flex-col bg-gray-950 text-white
                transition-opacity duration-200 ${closing ? 'opacity-0' : 'opacity-100'}`}
            role="dialog"
            aria-modal="true"
            aria-label={`Finish Mode: ${goal.title}`}
        >
            {/* ── Top bar ──────────────────────────────────────────────────── */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
                <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-amber-400 uppercase tracking-widest">
                        Finish Mode
                    </span>
                    <span className="text-xs text-gray-500">·</span>
                    <span className="text-xs text-gray-400 truncate max-w-xs">
                        {goal.title}
                    </span>
                </div>
                <button
                    onClick={handleClose}
                    className="text-gray-500 hover:text-gray-200 transition-colors p-1 rounded"
                    aria-label="Exit Finish Mode"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* ── Main content ─────────────────────────────────────────────── */}
            <div className="flex-1 overflow-y-auto px-6 py-8 max-w-2xl mx-auto w-full">

                {/* Percentage + microcopy ──────────────────────────────────── */}
                <div className="mb-10">
                    <div className="flex items-baseline gap-3 mb-2">
                        <span className="text-6xl font-bold tabular-nums text-white">
                            {pct}%
                        </span>
                        <span className="text-sm text-gray-400">complete</span>
                    </div>

                    {/* Thin amber progress track */}
                    <div className="w-full bg-gray-800 rounded-full h-1 mb-5">
                        <div
                            className="h-1 rounded-full bg-amber-400 transition-all duration-700"
                            style={{ width: `${pct}%` }}
                        />
                    </div>

                    <p className="text-base text-gray-300 leading-relaxed font-light">
                        {microcopy}
                    </p>

                    {/* Stall warning */}
                    {daysStalled >= 3 && (
                        <div className="mt-4 flex items-center gap-2 text-sm text-red-400">
                            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                            <span>
                                No progress for <strong>{daysStalled} days</strong>. This is where projects die.
                            </span>
                        </div>
                    )}
                </div>

                {/* Remaining tasks ─────────────────────────────────────────── */}
                <div>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-widest">
                            Remaining  ·  {remaining.length} task{remaining.length !== 1 ? 's' : ''}
                        </h2>
                    </div>

                    {remaining.length === 0 ? (
                        <div className="text-center py-12">
                            <p className="text-gray-500 text-sm">All tasks are complete.</p>
                            <p className="text-gray-600 text-xs mt-1">
                                Mark the goal as completed to record your finish.
                            </p>
                        </div>
                    ) : (
                        <ul className="space-y-3">
                            {remaining.map(task => (
                                <FinishModeTaskRow key={task.id} task={task} />
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* ── Footer ───────────────────────────────────────────────────── */}
            <div className="px-6 py-4 border-t border-gray-800 flex items-center justify-between max-w-2xl mx-auto w-full">
                <span className="text-xs text-gray-600">
                    Press <kbd className="px-1 py-0.5 bg-gray-800 rounded text-gray-500 font-mono text-xs">Esc</kbd> to exit
                </span>
                <button
                    onClick={handleClose}
                    className="text-xs text-gray-500 hover:text-gray-300 transition-colors"
                >
                    Exit Finish Mode
                </button>
            </div>
        </div>
    );
};

// ── Sub-component: individual task row ────────────────────────────────────────

const PRIORITY_DOT = {
    high: 'bg-red-500',
    medium: 'bg-amber-400',
    low: 'bg-green-500'
};

const FinishModeTaskRow = ({ task }) => {
    const dot = PRIORITY_DOT[task.priority] || 'bg-gray-500';

    return (
        <li className="flex items-start gap-3 p-3 rounded-lg bg-gray-900 border border-gray-800 hover:border-gray-700 transition-colors group">
            {/* Priority dot */}
            <span className={`mt-1.5 flex-shrink-0 w-2 h-2 rounded-full ${dot}`} aria-hidden="true" />

            <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-200 leading-snug">
                    {task.task || task.title || 'Untitled task'}
                </p>
                {task.owner && (
                    <p className="text-xs text-gray-600 mt-0.5">{typeof task.owner === 'object' ? task.owner.name : task.owner}</p>
                )}
                {task.dueDate && (
                    <p className="text-xs text-gray-600 mt-0.5">
                        Due {task.dueDate}
                    </p>
                )}
            </div>

            {/* Priority pill */}
            {task.priority && (
                <span className="flex-shrink-0 text-xs text-gray-600 capitalize mt-0.5">
                    {task.priority}
                </span>
            )}
        </li>
    );
};

export default FinishModePanel;
